import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { usePermissao } from '../hooks/use-permissao';

/**
 * Banner de propostas pendentes de atualização de SegmentoTemplate
 * (REQ-027 Fluxo 2 / AB#54). Renderizado globalmente (`App.tsx`) — em toda
 * tela, pra qualquer perfil — então só faz a chamada quando o usuário tem
 * `config.proposta_segmento:ler`. Para 1 proposta, deep link direto; para
 * várias, vai para a lista (a ser implementada se virar volume).
 *
 * [ADR-069 §13.19 Fatia 7 #891] Trocado o gate de `usuario.perfil ===
 * 'ADMIN_TENANT'` (enum legado, hard-coded) para `usePermissao().pode(
 * 'config.proposta_segmento:ler')` — sem atribuição RBAC ainda, `pode()`
 * sempre `true` (compatibilidade §4.3 regra 2), preservando o alcance atual
 * (só ADMIN_TENANT tinha `@Roles` nesta rota) até o backfill rodar.
 *
 * Re-busca em visibilitychange (consistente com TrialBanner) — sem heartbeat
 * porque proposta é raríssima.
 */

interface PropostaPendente {
  id: number;
  segmento_template: { nome: string };
}

export function PropostaSegmentoBanner(): JSX.Element | null {
  const navigate = useNavigate();
  const access_token = useAuthStore((s) => s.access_token);
  const { pode } = usePermissao();
  const [propostas, setPropostas] = useState<PropostaPendente[]>([]);

  const buscar = useCallback(async () => {
    if (!access_token || !pode('config.proposta_segmento:ler')) {
      // Funcional: não troca a referência quando já está vazio (um `[]` novo a cada render realimentava o efeito).
      setPropostas((atual) => (atual.length === 0 ? atual : []));
      return;
    }
    try {
      const res = await apiFetch('/minha-conta/propostas-segmento');
      if (!res.ok) return;
      setPropostas((await res.json()) as PropostaPendente[]);
    } catch {
      /* ignore */
    }
  }, [access_token, pode]);

  useEffect(() => {
    void buscar();
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void buscar();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [buscar]);

  if (propostas.length === 0) return null;

  const primeira = propostas[0]!;
  const titulo =
    propostas.length === 1
      ? `Há uma atualização disponível para o template do seu segmento "${primeira.segmento_template.nome}".`
      : `Há ${propostas.length} propostas de atualização de template pendentes.`;

  return (
    <div
      role="status"
      className="sticky top-0 z-30 flex w-full items-center justify-between gap-4 border-b border-amber-300 bg-amber-50 px-6 py-2.5 text-sm text-amber-900 shadow-sm"
    >
      <p className="font-medium">{titulo}</p>
      <button
        type="button"
        onClick={() => navigate(`/minha-conta/proposta-segmento/${primeira.id}`)}
        className="whitespace-nowrap rounded-md bg-amber-600 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-amber-700"
      >
        Ver detalhes
      </button>
    </div>
  );
}
